import { useState, useEffect, useRef } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Home, BookOpen, Layers, Compass, Users, Clock, Image, Heart, Lightbulb, HelpCircle, Settings, MoreHorizontal, X, ListOrdered } from "lucide-react";
import { Wordmark } from "./Wordmark";
import { InstallTip } from "./InstallTip";

const primary = [
  { to: "/home", label: "Home", icon: Home, end: true },
  { to: "/memories", label: "My Memories", icon: Layers },
  { to: "/story", label: "My Book", icon: BookOpen },
  { to: "/remember", label: "Help Me Remember", icon: Compass },
];

const organise = [
  { to: "/people", label: "People", icon: Users },
  { to: "/timeline", label: "Timeline", icon: Clock },
  { to: "/photos", label: "Photos", icon: Image },
  { to: "/book", label: "Finish & download", icon: ListOrdered },
];

const helpItems = [
  { to: "/family", label: "Family & Friends", icon: Heart },
  { to: "/tips", label: "Juliet's Tips", icon: Lightbulb },
  { to: "/help", label: "FAQ", icon: HelpCircle },
  { to: "/profile", label: "Settings", icon: Settings },
];

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [openMore, setOpenMore] = useState(false);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const ref = useRef(null);

  // Close the "More" menu whenever the route changes. On mobile, closing the sheet
  // on the NavLink's own onClick unmounted it before navigation could run, so taps
  // appeared to do nothing — letting the route change drive the close fixes that.
  useEffect(() => { setOpenMore(false); }, [location.pathname]);

  useEffect(() => {
    const onClick = (e) => {
      // The mobile "More" sheet lives outside `ref`; without this guard a mousedown on a
      // sheet link would close (unmount) the sheet before its click could navigate.
      if (e.target.closest && e.target.closest('[data-testid="mobile-more-sheet"]')) return;
      if (ref.current && !ref.current.contains(e.target)) setOpenMore(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // On mobile, hide the fixed bottom nav while a text field is focused so the
  // on-screen keyboard doesn't cover what the customer is typing.
  useEffect(() => {
    const isField = (el) => el && ["INPUT", "TEXTAREA"].includes(el.tagName);
    const onFocusIn = (e) => {
      if (isField(e.target)) {
        setKeyboardOpen(true);
        setTimeout(() => {
          try { e.target.scrollIntoView({ block: "center", behavior: "smooth" }); } catch {}
        }, 350);
      }
    };
    const onFocusOut = (e) => { if (isField(e.target)) setKeyboardOpen(false); };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => { document.removeEventListener("focusin", onFocusIn); document.removeEventListener("focusout", onFocusOut); };
  }, []);

  const linkClass = ({ isActive }) =>
    `px-3 py-2 rounded-full text-sm font-medium transition-colors ${
      isActive ? "bg-teal text-cream" : "text-subtle hover:text-teal hover:bg-sagelight"
    }`;

  return (
    <div className="min-h-screen bg-cream pb-24 md:pb-0">
      <header className="sticky top-0 z-30 bg-cream/85 backdrop-blur-md border-b border-line">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <button data-testid="brand-home-link" onClick={() => navigate("/home")} className="group">
            <Wordmark size="text-xl sm:text-2xl" className="group-hover:opacity-80 transition-opacity" />
          </button>

          <nav className="hidden md:flex items-center gap-1">
            {primary.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`} className={linkClass}>
                {n.label}
              </NavLink>
            ))}
            <div className="relative" ref={ref}>
              <button data-testid="nav-more-btn" onClick={() => setOpenMore((o) => !o)}
                className={`px-3 py-2 rounded-full text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${openMore ? "bg-sagelight text-teal" : "text-subtle hover:text-teal hover:bg-sagelight"}`}>
                More <MoreHorizontal size={16} />
              </button>
              {openMore && (
                <div data-testid="nav-more-menu" className="absolute right-0 mt-2 w-60 bg-white rounded-2xl border border-line shadow-lg py-2 z-40">
                  <p className="px-4 pt-1 pb-1 text-[11px] uppercase tracking-wide text-sage font-semibold">Organise my story</p>
                  {organise.map((n) => (
                    <NavLink key={n.to + n.label} to={n.to} onClick={() => setOpenMore(false)}
                      data-testid={`more-${n.label.toLowerCase().replace(/[\s&]+/g, "-")}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isActive ? "text-teal bg-sagelight" : "text-charcoal hover:bg-cream2"}`}>
                      <n.icon size={17} className="text-sage" /> {n.label}
                    </NavLink>
                  ))}
                  <div className="my-1 border-t border-line" />
                  {helpItems.map((n) => (
                    <NavLink key={n.to} to={n.to} onClick={() => setOpenMore(false)}
                      data-testid={`more-${n.label.toLowerCase().replace(/[\s&]+/g, "-")}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isActive ? "text-teal bg-sagelight" : "text-charcoal hover:bg-cream2"}`}>
                      <n.icon size={17} className="text-sage" /> {n.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          </nav>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 relative z-10">
        <Outlet />
      </main>

      {/* Bottom nav (mobile) — hidden while typing so the keyboard doesn't cover the text */}
      <nav className={`md:hidden fixed bottom-0 inset-x-0 z-30 bg-cream/95 backdrop-blur-md border-t border-line ${keyboardOpen ? "hidden" : ""}`}>
        <div className="grid grid-cols-5">
          {primary.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              data-testid={`bottomnav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-2.5 text-[11px] font-medium transition-colors ${isActive ? "text-coral" : "text-subtle"}`}>
              <n.icon size={22} />
              {n.label.replace("My ", "").replace("Help Me ", "")}
            </NavLink>
          ))}
          <button data-testid="bottomnav-more-btn" onClick={() => setOpenMore(true)}
            className="flex flex-col items-center justify-center gap-1 py-2.5 text-[11px] font-medium text-subtle">
            <MoreHorizontal size={22} /> More
          </button>
        </div>
      </nav>

      {/* Mobile "More" sheet */}
      {openMore && (
        <div className="md:hidden fixed inset-0 z-40" data-testid="mobile-more-sheet">
          <div className="absolute inset-0 bg-charcoal/40" onClick={() => setOpenMore(false)} />
          <div className="absolute bottom-0 inset-x-0 bg-cream rounded-t-3xl p-5 pb-8 animate-fade-up">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-serif text-xl text-charcoal">More</h3>
              <button data-testid="mobile-more-close" onClick={() => setOpenMore(false)} className="p-2 text-subtle"><X size={20} /></button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <p className="col-span-2 text-[11px] uppercase tracking-wide text-sage font-semibold mt-1">Organise my story</p>
              {organise.map((n) => (
                <NavLink key={n.to + n.label} to={n.to}
                  data-testid={`mobile-more-${n.label.toLowerCase().replace(/[\s&]+/g, "-")}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white border border-line text-sm text-charcoal">
                  <n.icon size={18} className="text-sage" /> {n.label}
                </NavLink>
              ))}
              <div className="col-span-2 h-px bg-line my-1" />
              {helpItems.map((n) => (
                <NavLink key={n.to} to={n.to}
                  data-testid={`mobile-more-${n.label.toLowerCase().replace(/[\s&]+/g, "-")}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white border border-line text-sm text-charcoal">
                  <n.icon size={18} className="text-sage" /> {n.label}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}

      <InstallTip />
    </div>
  );
}
